import * as React from "react";
import { Redirect, Route, RouteProps, Switch } from "react-router-dom";

import { FindAppNavigationItemFromRoute } from "./app-navigation";
import { useAuth } from "./contexts";
import { ERoutes } from "./ERoutes";
import {
  ArticleItemPage,
  ArticleListPage,
  BreedGroupItemPage,
  BreedGroupsListPage,
  BreedItemPage,
  BreedsListPage,
  CharacteristicsItemPage,
  CharacteristicsListPage,
  CharacteristicValuesItemPage,
  CharacteristicValuesListPage,
  ClinicItemPage,
  ClinicListPage,
  ClubItemPage,
  ClublistPage,
  ClubRoleItemPage,
  ClubRoleListPage,
  ColorItemPage,
  ColorListPage,
  CountryItemPage,
  CountryListPage,
  DocumentTemplateItemPage,
  DocumentTemplateListPage,
  EmailItemPage,
  EmailListPage,
  FciExportPage,
  FciImportPage,
  HealthPackItemPage,
  HealthPackListPage,
  HomePage,
  KennelItemPage,
  KennelListPage,
  MembershipRenewalListPage,
  PedigreeDuplicataRequestItemPage,
  PedigreeDuplicataRequestList,
  PedigreeRequestListPage,
  PermissionListPage,
  RequestListPage,
  SubActivityItemPage,
  SubActivityListPage,
  TranslationEditPage,
  UnauthorizedPage,
  UserItemPage,
  UserListPage,
  WorkbookTypeItemPage,
  WorkbookTypeListPage,
  ReportPage,
  ExpatriationRequestListPage,
} from "./pages";
import { PersonItemPage, PersonListPage, PersonSplitItemPage } from "./pages/contacts/person";
import { MyTodoListItemPage, UnassignedRequestItemPage } from "./pages/dashboard";
import { CalendarYearItemPage, CalendarYearListPage } from "./pages/dog/calendarYear";
import { DashboardPage } from "./pages";
import { DogItemPage, DogListPage } from "./pages/dog/dog";
import { DogFusionItemPage, PersonFusionItemPage } from "./pages/fusion";
import { AppealDysplasiaList, ComissionDysplasiaItemPage, ComissionDysplasiaList } from "./pages/dysplasie";
import { ComissionGandDysplasiaList } from "./pages/dysplasie/comissionGand/ComissionGandDysplasiaList";
import {
  BroodRequestItemPage,
  BroodRequestListPage,
  DogOwnershipChangeRequestItemPage,
  DogOwnershipChangeRequestList,
  DysplasiaRequestItemPage,
  DysplasiaRequestList,
  ImportRequestItemPage,
  ImportRequestList,
  MembershipRequestItemPage,
  MembershipRequestListPage,
  PedigreeRequestItemPage,
  TempRSHRequestItemPage,
  TempRSHRequestList,
  TitleRequestItemPage,
  TitleRequestList,
  WorkAttestationRequestItemPage,
  WorkAttestationRequestListPage,
  WorkbookRequestItemPage,
  WorkbookRequestListPage,
  ExpatriationRequestItemPage,
} from "./pages";
import { AffixRequestItemPage, AffixRequestList } from "./pages/request/affixRequest";
import { CompetitionRequestItemPage, CompetitionRequestList } from "./pages/request/competitionRequest";
import { HealthTestRequestItemPage, HealthTestRequestList } from "./pages/request/healthTestRequest";
import { MiscellaneousRequestItemPage } from "./pages/request/miscellaneousRequest";
import { MiscellaneousRequestList } from "./pages/request/miscellaneousRequest";
import { ActivityItemPage, ActivityListPage } from "./pages/sport/activity";
import { CompetitionItemPage, CompetitionList } from "./pages/sport/competition";
import { SectionItemPage, SectionListPage } from "./pages/sport/sections";
import { TitleTypeItemPage, TitleTypeList } from "./pages";
import { TestDevPage } from "./pages/TestDev";
import { MailLogListPage } from "./pages/mail-log/MailLogListPage";
import { PasswordPage } from "./pages/login/PasswordPage";
import { ReprintPedigreeList } from "./pages/administration/reprint";
import { LanguageListPage } from "./pages/administration/language/list/LanguageListPage";
import { LanguageItemPage } from "./pages/administration/language/item/LanguageItemPage";

interface IProtectedRouteProps extends RouteProps {
  module?: string;
}

export const ProtectedRoute: React.FC<IProtectedRouteProps> = ({ module, ...routeProps }) => {
  const { matchModule } = useAuth();

  const currentpathRouteModule = React.useMemo(() => {
    if (module) {
      return module;
    } else {
      const found = FindAppNavigationItemFromRoute(routeProps.path as string);
      return found?.route.split("/")[1];
    }
  }, [module, routeProps]);

  const activeModule = React.useMemo(() => {
    let route = currentpathRouteModule;
    route = route ? route : "" + (routeProps.path as string).split("/")[1];
    return matchModule(route);
  }, [currentpathRouteModule, matchModule, routeProps.path]);

  if (activeModule) {
    return <Route {...routeProps} />;
  } else {
    return null;
  }
};

export const AppRouter = () => {
  const { userPermissions } = useAuth();
  const hasAnyPermission = React.useMemo(() => userPermissions?.length > 0, [userPermissions]);
  return (
    <Switch>
      <Route path={`${ERoutes.testdev}`} component={TestDevPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.home}`} component={HomePage} exact={true} />
      <ProtectedRoute path={`${ERoutes.dashboard}`} component={DashboardPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.activity}`} component={ActivityListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.activity}/:id/:tab/:state?`} component={ActivityItemPage} />
      <ProtectedRoute path={`${ERoutes.breeds}`} component={BreedsListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.breeds}/:id/:tab/:state?`} component={BreedItemPage} />
      <ProtectedRoute path={`${ERoutes.breedGroups}`} component={BreedGroupsListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.breedGroups}/:id/:tab/:state?`} component={BreedGroupItemPage} />
      <ProtectedRoute path={`${ERoutes.color}`} component={ColorListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.color}/:id/:tab/:state?`} component={ColorItemPage} />
      <ProtectedRoute path={`${ERoutes.country}`} component={CountryListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.country}/:id/:tab/:state?`} component={CountryItemPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.kennel}`} component={KennelListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.kennel}/:id/:tab/:state?`} component={KennelItemPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.dog}`} component={DogListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.dog}/:id/:tab/:state?`} component={DogItemPage} exact={false} />
      <ProtectedRoute path={`${ERoutes.characteristics}`} component={CharacteristicsListPage} exact={true} />
      <ProtectedRoute
        path={`${ERoutes.characteristics}/:id/:tab/:state?`}
        component={CharacteristicsItemPage}
        exact={false}
      />
      <ProtectedRoute path={`${ERoutes.characteristicValues}`} component={CharacteristicValuesListPage} exact={true} />
      <ProtectedRoute
        path={`${ERoutes.characteristicValues}/:id/:tab/:state?`}
        component={CharacteristicValuesItemPage}
        exact={false}
      />
      <Route path={`${ERoutes.changePassword}`} component={PasswordPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.workbookType}`} component={WorkbookTypeListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.workbookType}/:id/:tab/:state?`} component={WorkbookTypeItemPage} />
      <ProtectedRoute path={`${ERoutes.section}`} component={SectionListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.section}/:id/:tab/:state?`} component={SectionItemPage} />
      <ProtectedRoute path={`${ERoutes.calendarYear}`} component={CalendarYearListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.calendarYear}/:id/:tab/:state?`} component={CalendarYearItemPage} />
      <ProtectedRoute path={`${ERoutes.subActivity}`} component={SubActivityListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.subActivity}/:id/:tab/:state?`} component={SubActivityItemPage} />
      <ProtectedRoute path={`${ERoutes.club}`} component={ClublistPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.club}/:id/:tab/:state?`} component={ClubItemPage} />
      <ProtectedRoute path={`${ERoutes.translations}`} component={TranslationEditPage} />
      <ProtectedRoute path={`${ERoutes.mailAdmin}`} component={MailLogListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.people}`} component={PersonListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.people}/:id/:tab/:state?`} component={PersonItemPage} />
      <ProtectedRoute path={`${ERoutes.clubRoles}`} component={ClubRoleListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.clubRoles}/:id/:tab/:state?`} component={ClubRoleItemPage} />
      <ProtectedRoute path={`${ERoutes.titleType}`} component={TitleTypeList} exact={true} />
      <ProtectedRoute path={`${ERoutes.titleType}/:id/:tab/:state?`} component={TitleTypeItemPage} />
      <ProtectedRoute path={`${ERoutes.requests}`} component={RequestListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.pedigreeDuplicata}`} component={PedigreeDuplicataRequestList} exact={true} />
      <ProtectedRoute
        path={`${ERoutes.pedigreeDuplicata}/:id/:tab/:state/:masterRequestId?`}
        component={PedigreeDuplicataRequestItemPage}
      />
      <ProtectedRoute path={`${ERoutes.rish}`} component={PedigreeRequestListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.rish}/:id/:tab/:state?`} component={PedigreeRequestItemPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.rsh}`} component={DogOwnershipChangeRequestList} exact={true} />
      <ProtectedRoute path={`${ERoutes.rsh}/:id/:tab/:state?`} component={DogOwnershipChangeRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.clinic}`} component={ClinicListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.clinic}/:id/:tab/:state?`} component={ClinicItemPage} />
      <ProtectedRoute path={`${ERoutes.titleRequests}`} component={TitleRequestList} exact={true} />
      <ProtectedRoute path={`${ERoutes.titleRequests}/:id/:tab/:state?`} component={TitleRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.expatriationRequest}`} component={ExpatriationRequestListPage} exact={true} />
      <ProtectedRoute
        path={`${ERoutes.expatriationRequest}/:id/:tab/:state`}
        component={ExpatriationRequestItemPage}
        exact={true}
      />
      <ProtectedRoute path={`${ERoutes.workbookRequest}`} component={WorkbookRequestListPage} exact={true} />
      <ProtectedRoute
        path={`${ERoutes.workbookRequest}/:id(0)/:tab/:state/:masterRequestId`}
        component={WorkbookRequestItemPage}
      />
      <ProtectedRoute path={`${ERoutes.workbookRequest}/:id/:tab/:state?`} component={WorkbookRequestItemPage} />
      <ProtectedRoute
        path={`${ERoutes.workAttestationRequest}`}
        component={WorkAttestationRequestListPage}
        exact={true}
      />
      <ProtectedRoute
        path={`${ERoutes.workAttestationRequest}/:id/:tab/:state?`}
        component={WorkAttestationRequestItemPage}
      />
      <ProtectedRoute path={`${ERoutes.user}`} component={UserListPage} exact={true} />
      <ProtectedRoute path={`${ERoutes.user}/:id/:tab/:state?`} component={UserItemPage} />
      <ProtectedRoute path={`${ERoutes.tempRsh}`} component={TempRSHRequestList} exact={true} />
      <ProtectedRoute path={`${ERoutes.tempRsh}/:id/:tab/:state?`} component={TempRSHRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.importRequest}`} component={ImportRequestList} exact={true} />
      <ProtectedRoute path={`${ERoutes.importRequest}/:id/:tab/:state?`} component={ImportRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.permission}`} component={PermissionListPage} />
      <ProtectedRoute path={`${ERoutes.unassignedRequests}/:tab?`} component={UnassignedRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.assignedRequests}/:tab?`} component={MyTodoListItemPage} />
      <ProtectedRoute path={`${ERoutes.article}`} component={ArticleListPage} exact />
      <ProtectedRoute path={`${ERoutes.article}/:id/:tab/:state?`} component={ArticleItemPage} />
      <ProtectedRoute path={`${ERoutes.dysplasiaRequest}`} component={DysplasiaRequestList} exact={true} />
      <ProtectedRoute
        path={`${ERoutes.dysplasiaRequest}/:id(0)/:tab/:state/:masterRequestId`}
        component={DysplasiaRequestItemPage}
      />
      <ProtectedRoute path={`${ERoutes.dysplasiaRequest}/:id/:tab/:state?`} component={DysplasiaRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.membershipRequest}`} component={MembershipRequestListPage} exact />
      <ProtectedRoute path={`${ERoutes.membershipRequest}/:id/:tab/:state?`} component={MembershipRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.membershipRenewal}`} component={MembershipRenewalListPage} />
      <ProtectedRoute path={`${ERoutes.appealDysplasia}`} component={AppealDysplasiaList} exact />
      <ProtectedRoute
        path={`${ERoutes.appealDysplasia}/:id/:tab/:state?`}
        component={ComissionDysplasiaItemPage}
        exact
      />
      <ProtectedRoute path={`${ERoutes.commissionDysplasia}`} component={ComissionDysplasiaList} exact />
      <ProtectedRoute
        path={`${ERoutes.commissionDysplasia}/:id/:tab/:state?`}
        component={ComissionDysplasiaItemPage}
        exact
      />
      $
      <ProtectedRoute path={`${ERoutes.gentCommission}`} component={ComissionGandDysplasiaList} exact />
      <ProtectedRoute
        path={`${ERoutes.gentCommission}/:id/:tab/:state?`}
        component={ComissionDysplasiaItemPage}
        exact
      />
      <ProtectedRoute path={`${ERoutes.competitionRequest}`} component={CompetitionRequestList} exact />
      <ProtectedRoute path={`${ERoutes.competitionRequest}/:id/:tab/:state?`} component={CompetitionRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.competition}`} component={CompetitionList} exact />
      <ProtectedRoute path={`${ERoutes.competition}/:id/:tab/:state?`} component={CompetitionItemPage} exact />
      <ProtectedRoute path={`${ERoutes.affixRequest}`} component={AffixRequestList} exact />
      <ProtectedRoute path={`${ERoutes.affixRequest}/:id/:tab/:state/:type`} component={AffixRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.affixRequest}/:id/:tab/:state(view)/:type`} component={AffixRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.affixRequest}/:id/:tab/:state?`} component={AffixRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.affixExport}/:tab?/:state?`} component={FciExportPage} />
      <ProtectedRoute path={`${ERoutes.affixImport}/:tab?/:state?`} component={FciImportPage} />
      <ProtectedRoute path={`${ERoutes.healthPack}`} component={HealthPackListPage} exact />
      <ProtectedRoute path={`${ERoutes.healthPack}/:id/:tab/:state`} component={HealthPackItemPage} />
      <ProtectedRoute path={`${ERoutes.healthTestRequest}`} component={HealthTestRequestList} exact />
      <ProtectedRoute
        path={`${ERoutes.healthTestRequest}/:id(0)/:tab/:state/:masterRequestId`}
        component={HealthTestRequestItemPage}
      />
      <ProtectedRoute path={`${ERoutes.healthTestRequest}/:id/:tab/:state`} component={HealthTestRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.broodRequest}`} component={BroodRequestListPage} exact />
      <ProtectedRoute path={`${ERoutes.broodRequest}/:id/:tab/:state`} component={BroodRequestItemPage} />
      <ProtectedRoute path={`${ERoutes.email}`} component={EmailListPage} exact />
      <ProtectedRoute path={`${ERoutes.email}/:id/:tab/:state`} component={EmailItemPage} />
      <ProtectedRoute path={`${ERoutes.documentTemplate}`} component={DocumentTemplateListPage} exact />
      <ProtectedRoute path={`${ERoutes.documentTemplate}/:id/:tab/:state`} component={DocumentTemplateItemPage} />
      <ProtectedRoute path={`${ERoutes.miscellaneousRequest}`} component={MiscellaneousRequestList} exact />
      <ProtectedRoute
        path={`${ERoutes.miscellaneousRequest}/:id(0)/:tab/:state/:masterRequestId`}
        component={MiscellaneousRequestItemPage}
      />
      <ProtectedRoute
        path={`${ERoutes.miscellaneousRequest}/:id/:tab/:state`}
        component={MiscellaneousRequestItemPage}
      />
      <ProtectedRoute path={`${ERoutes.reprintPedigree}`} component={ReprintPedigreeList} />
      <ProtectedRoute path={`${ERoutes.personSplit}/:id/:tab/:state?`} component={PersonSplitItemPage} exact />
      <ProtectedRoute path={`${ERoutes.personFusion}/:id/:tab/:state?`} component={PersonFusionItemPage} />
      <ProtectedRoute path={`${ERoutes.dogFusion}/:id/:tab/:state?`} component={DogFusionItemPage} />
      <ProtectedRoute path={`${ERoutes.report}`} component={ReportPage} />
      <ProtectedRoute path={`${ERoutes.language}/:id/:tab/:state?`} component={LanguageItemPage} exact />
      <ProtectedRoute path={`${ERoutes.language}`} component={LanguageListPage} />
      {!hasAnyPermission && <Route path={`${ERoutes.unauthorized}`} component={UnauthorizedPage} exact />}
      {hasAnyPermission ? <Redirect to={ERoutes.home} /> : <Redirect to={ERoutes.unauthorized} />}
    </Switch>
  );
};
